import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
} from "@mui/material";
import { showSuccessMsg, showErrorMsg } from "../../utils/toast";
import api from "../../services/api";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import Scrollbar from "../../components/table/Scrollbar";
import FormSearch from "../../components/FormSearch";
import ListHead from "../../components/table/ListHead";
import SearchNotFound from "../../components/table/SearchNotFound";
import CircularLoader from "../../components/circularLoader";
import UpdateMinPayoutDialog from "./UpdateMinPayoutDialog";

const TABLE_HEAD = [
  { id: "merchant_name", label: "Merchant Name", alignRight: false },
  { id: "account_name", label: "Account Name", alignRight: false },
  { id: "source", label: "Source", alignRight: false },
  { id: "action", label: "Action", alignRight: true },
];

const DEFAULT_FILTER = { source: "all" };

export default function MerchantDetail() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [currentFilter, setCurrentFilter] = useState({});
  const { setGlobalLoading } = useLoadingSpinnerContext();

  const schema = [
    {
      type: "text",
      label: "Merchant Name",
      param: "merchant_name",
    },
    {
      type: "text",
      label: "Account Name",
      param: "account_name",
    },
    {
      type: "select",
      label: "Source",
      param: "source",
      options: [
        { name: "All", id: "all" },
        { name: "GW2", id: "gw2" },
        { name: "GW3", id: "gw3" },
      ],
    },
  ];

  const onFilterSubmit = (newParams) => {
    setCurrentFilter(newParams);
    setPage(0);
  };

  const fetchData = async () => {
    setLoaded(false);
    try {
      const response = await api.get("/payout/ingestor_merchant/merchant_list/", {
        params: {
          page: page + 1,
          page_size: rowsPerPage,
          ...currentFilter,
        },
      });
      if (response?.status === 200) {
        setData(response.data.results || []);
        setTotal(response.data.count || 0);
      } else {
        showErrorMsg("Failed to fetch merchants");
      }
    } catch (error) {
      console.error(error);
      showErrorMsg("Failed to fetch merchants");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, currentFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenUpdate = async (merchant) => {
    setGlobalLoading(true);
    try {
      const response = await api.get(`/payout/ingestor_merchant/merchant_payout/${merchant.merchant_id}/`);
      if (response && response.status === 200) {
        setSelectedMerchant(response.data);
        setOpenUpdateModal(true);
      } else {
        showErrorMsg("Failed to fetch merchant details");
      }
    } catch (error) {
      console.error(error);
      showErrorMsg("Failed to fetch merchant details");
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleCloseUpdate = () => {
    setOpenUpdateModal(false);
    setSelectedMerchant(null);
  };

  const handleUpdateMinPayout = async (merchantId, minPayoutAmount) => {
    try {
      const response = await api.post("/payout/ingestor_merchant/update_min_payout/", {
        merchant_id: merchantId,
        minimum_payout_amount: minPayoutAmount,
      });
      if (response && response.status === 200) {
        showSuccessMsg("Triggered update successfully");
        handleCloseUpdate();
      } else {
        showErrorMsg("Failed to trigger update");
      }
    } catch (error) {
      console.error(error);
      showErrorMsg("Failed to trigger update");
    }
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - total) : 0;
  const isNotFound = data.length === 0;

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!loaded}
        onSearch={onFilterSubmit}
        defaultValue={DEFAULT_FILTER}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  headLabel={TABLE_HEAD}
                  rowCount={data.length}
                  checkbox={false}
                />
                <TableBody>
                  {data.map((row) => {
                    const { merchant_id, merchant_name, account_name, source } = row;
                    return (
                      <TableRow hover key={merchant_id + source} tabIndex={-1}>
                        <TableCell component="th" scope="row">
                          {merchant_name}
                        </TableCell>
                        <TableCell align="left">{account_name}</TableCell>
                        <TableCell align="left">{source?.toUpperCase()}</TableCell>
                        <TableCell align="right">
                          {source === "gw2" && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleOpenUpdate(row)}
                            >
                              Update
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={4} />
                    </TableRow>
                  )}
                </TableBody>
                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={4} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          {openUpdateModal && selectedMerchant && (
            <UpdateMinPayoutDialog
              open={openUpdateModal}
              onClose={handleCloseUpdate}
              merchant={selectedMerchant}
              onSubmit={handleUpdateMinPayout}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Box>
  );
}
